import React, { useMemo, useState } from 'react';

export default function PairsTab({ draws }) {
  const [searchNum, setSearchNum] = useState('');

  const allPairs = useMemo(() => {
    const pairCounts = {};
    draws.forEach(draw => {
      const main = draw.main;
      for (let i = 0; i < main.length; i++) {
        for (let j = i + 1; j < main.length; j++) {
          const a = Math.min(main[i], main[j]);
          const b = Math.max(main[i], main[j]);
          const key = `${a}-${b}`;
          pairCounts[key] = (pairCounts[key] || 0) + 1;
        }
      }
    });
    return Object.entries(pairCounts)
      .map(([pair, count]) => ({ pair, count, nums: pair.split('-').map(Number) }))
      .sort((a, b) => b.count - a.count);
  }, [draws]);

  const searchFilter = parseInt(searchNum, 10);
  const isSearching = !isNaN(searchFilter) && searchNum !== '';

  const displayPairs = useMemo(() => {
    if (isSearching) {
      return allPairs.filter(p => p.nums.includes(searchFilter)).slice(0, 30);
    }
    return allPairs.slice(0, 30);
  }, [allPairs, isSearching, searchFilter]);

  const maxCount = displayPairs[0]?.count || 1;

  return (
    <>
      <p className="section-label">Most frequent number pairs</p>
      <div className="pairs-controls">
        <input
          type="number"
          className="trends-input"
          placeholder="Search by number..."
          value={searchNum}
          onChange={(e) => setSearchNum(e.target.value)}
        />
        {isSearching && (
          <button className="mode-btn" onClick={() => setSearchNum('')}>Clear</button>
        )}
        <span className="pairs-hint">
          {isSearching ? `Pairs containing #${searchFilter}` : 'Top 30 pairs overall'}
        </span>
      </div>

      <table className="gap-table pairs-table">
        <thead>
          <tr>
            <th>Rank</th>
            <th>Pair</th>
            <th>Count</th>
            <th>Frequency</th>
          </tr>
        </thead>
        <tbody>
          {displayPairs.map((p, i) => (
            <tr key={p.pair}>
              <td style={{ fontWeight: 700 }}>{i + 1}</td>
              <td>
                <div className="pairs-balls">
                  <div className="pairs-ball">{p.nums[0]}</div>
                  <span className="pairs-separator">&</span>
                  <div className="pairs-ball">{p.nums[1]}</div>
                </div>
              </td>
              <td>{p.count}x</td>
              <td>
                <div className="pairs-freq-cell">
                  <span
                    className="gap-bar"
                    style={{ width: `${Math.round((p.count / maxCount) * 100)}%` }}
                  ></span>
                  <span className="pairs-pct">{Math.round((p.count / maxCount) * 100)}%</span>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {displayPairs.length === 0 && (
        <p className="pairs-empty">No pairs found for #{searchFilter}</p>
      )}
      <p className="section-note">
        Co-occurrence counts reflect historical coincidence only. In a random draw, no pair of numbers has any causal relationship — high pair frequency is a statistical artifact of a large dataset, not a pattern that predicts future draws.
      </p>
    </>
  );
}
